'use client';

import { createContext, useContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useSession, signOut } from 'next-auth/react';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useTranslationApi } from '@/hooks/use-translation-api';
import { useUserApi } from '@/hooks/use-user-api';
import type { Translation } from '@/schemas/translation.schema';
import type { User } from '@/schemas/user.schema';
import type { Organization } from '@/schemas/organization.schema';
import { installClientErrorListeners, setClientErrorUser } from '@/lib/report-client-error';

interface AppContextType {
  locale: string;
  isRTL: boolean;
  user: User | null;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isOperator: boolean;
  userOrganization: Pick<Organization, 'id' | 'name' | 'logoUrl'> | null;
  /** Push freshly saved branding straight into the header, without a reload. */
  setUserOrganization: (organization: Pick<Organization, 'id' | 'name' | 'logoUrl'>) => void;
  translationMap: Record<string, string>;
  translationsLoaded: boolean;
  translateEntity: (entity: string, entityId: string, field: string, fallback?: string) => string;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const locale = useLocale();
  const isRTL = locale === 'he' || locale === 'ar';
  const pathname = usePathname();
  const { data: session } = useSession();
  const { user, isSuperAdmin, isAdmin, isOperator, isLoading: isUserLoading } = useCurrentUser();
  const hasSignedOutRef = useRef(false);

  // Browser errors that no error boundary sees (handlers, timers, promises) go
  // to the backend; see lib/report-client-error.ts.
  useEffect(() => installClientErrorListeners(), []);
  useEffect(() => setClientErrorUser(user?.id), [user?.id]);

  useEffect(() => {
    // Already on the auth/login page — signing out here would redirect back
    // to /app and re-trigger this effect, causing an infinite reload loop.
    const isAuthPage = pathname.endsWith('/app') || pathname.endsWith('/login');

    if (
      !hasSignedOutRef.current &&
      !isAuthPage &&
      (session?.error === 'RefreshTokenExpired' || session?.error === 'RefreshTokenError')
    ) {
      hasSignedOutRef.current = true;
      signOut({ callbackUrl: `/${locale}/app` });
    }
  }, [session?.error, locale, pathname]);
  // The session's organization rides along in the JWT, so it goes stale the moment
  // anyone edits the logo or the name. Fetched branding wins once it lands, but only
  // while it still describes the signed-in user's own organization — that id check is
  // also what lets the admin screen push any organization it saves without filtering.
  const [fetchedOrganization, setFetchedOrganization] =
    useState<Pick<Organization, 'id' | 'name' | 'logoUrl'> | null>(null);
  const { getById: getUserById } = useUserApi();

  const userOrganization =
    fetchedOrganization && fetchedOrganization.id === user?.organizationId
      ? fetchedOrganization
      : user?.organization || null;

  useEffect(() => {
    if (!user?.id || !user?.organizationId) return;

    let isActive = true;

    getUserById(user.id)
      .then((response) => {
        const organization = response?.data?.organization;
        if (!isActive || !organization) return;
        setFetchedOrganization({
          id: organization.id,
          name: organization.name,
          logoUrl: organization.logoUrl ?? null,
        });
      })
      .catch((error) => {
        console.error('Failed to load organization branding', error);
      });

    return () => {
      isActive = false;
    };
  // getUserById is intentionally omitted for the same reason as getTranslations below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.organizationId]);

  const { getList: getTranslations } = useTranslationApi();
  const [translationMap, setTranslationMap] = useState<Record<string, string>>({});
  const [translationsLoaded, setTranslationsLoaded] = useState(false);

  const translationEntities = useMemo(() => ['commodity_type'], []);

  useEffect(() => {
    let isActive = true;
    const loadTranslations = async () => {
      if (isUserLoading || !user) return;
      setTranslationsLoaded(false);
      try {
        const responses = await Promise.all(
          translationEntities.map((entity) =>
            getTranslations({ entity, locale })
          )
        );
        const nextMap: Record<string, string> = {};
        responses.forEach((response) => {
          const items = response?.data ?? [];
          items.forEach((translation: Translation) => {
            const key = `${translation.entity}::${translation.entityId}::${translation.field}`;
            nextMap[key] = translation.text;
          });
        });
        if (isActive) {
          setTranslationMap(nextMap);
          setTranslationsLoaded(true);
        }
      } catch (error) {
        if (isActive) {
          setTranslationMap({});
          setTranslationsLoaded(true);
        }
      }
    };

    loadTranslations();
    return () => {
      isActive = false;
    };
  // getTranslations is intentionally omitted — it recreates on every token refresh
  // and would cause duplicate 401 requests during session hydration.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoading, locale, translationEntities, user]);

  const translateEntity = useCallback(
    (entity: string, entityId: string, field: string, fallback?: string) => {
      const key = `${entity}::${entityId}::${field}`;
      return translationMap[key] ?? fallback ?? '';
    },
    [translationMap]
  );

  const value = useMemo<AppContextType>(
    () => ({
      locale,
      isRTL,
      user,
      isSuperAdmin,
      isAdmin,
      isOperator,
      userOrganization,
      setUserOrganization: setFetchedOrganization,
      translationMap,
      translationsLoaded,
      translateEntity,
    }),
    [locale, isRTL, user, isSuperAdmin, isAdmin, isOperator, userOrganization, translationMap, translationsLoaded, translateEntity]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
